import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { scrollLog, scrollTick } from "@/lib/scroll-profile";

const DEFAULT_THRESHOLD_PX = 2000;
const DEFAULT_COOLDOWN_MS = 150;
const RESET_GUARD_MS = 200;

// ─── Exported decision functions (pure, testable) ─────────────────

export type TopAction = "shift-up" | "bump" | "none";
export type BottomAction = "shift-down" | "none";

/** Decide what to do when the top edge is approached. */
export function decideTopAction(
  hasOlderTrimmed: boolean,
  hasMoreOnServer: boolean,
  bumpInFlight: boolean,
): TopAction {
  if (hasOlderTrimmed) return "shift-up";
  if (hasMoreOnServer && !bumpInFlight) return "bump";
  return "none";
}

/** Decide what to do when the bottom edge is approached. */
export function decideBottomAction(hasNewerTrimmed: boolean): BottomAction {
  if (hasNewerTrimmed) return "shift-down";
  return "none";
}

interface UseLoadMoreOptions {
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Older loaded messages exist in Zero slice but are trimmed from render window */
  hasOlderTrimmed: boolean;
  /** Server has more history beyond what Zero has synced */
  hasMoreOnServer: boolean;
  /** Newer loaded messages exist in Zero slice but are trimmed from render window */
  hasNewerTrimmed: boolean;
  /** Shift the render window backward (instant, client-side) */
  onShiftWindowUp: () => void;
  /** Shift the render window forward (instant, client-side) */
  onShiftWindowDown: () => void;
  /** Bump the Zero query limit (async, network) */
  onBumpLimit: () => void;
  /** Pixel threshold from edge to trigger load-more */
  thresholdPx?: number;
  /** Minimum ms between shift calls */
  cooldownMs?: number;
  /** Whether a Zero limit bump is currently in flight (owned by caller) */
  bumpInFlightRef: RefObject<boolean>;
  /**
   * Identity of the rendered collection (e.g. channelId). When this
   * changes the scroll detector ignores scroll events for ~200ms —
   * programmatic scroll restoration after channel-switch / jump-nav
   * fires scroll events that would otherwise be interpreted as
   * user-initiated scrolling.
   */
  resetKey?: string | null;
}

/**
 * Scroll-event-driven load-more for both directions.
 *
 * Top (scroll-up past hysteresis, within thresholdPx of slice top):
 * 1. hasOlderTrimmed → onShiftWindowUp (reveal already-loaded older messages)
 * 2. else hasMoreOnServer → onBumpLimit (fetch from server)
 * 3. else → no-op
 *
 * Bottom (scroll-down past hysteresis, within thresholdPx of slice bottom):
 * 1. hasNewerTrimmed → onShiftWindowDown (reveal already-loaded newer messages)
 * 2. else → no-op (reaching the live tail is the near-bottom callback's job)
 *
 * Why scroll-event for both: with a small initial slice (channel switch
 * with saved anchor lands at e.g. 30 rows in a 696-msg query) BOTH
 * sentinels sit inside the IntersectionObserver's 2000px rootMargin
 * from mount. The observer fires once and never again — useful only
 * for transitions, useless for "is the user actively scrolling toward
 * this edge". A scroll-event listener fires continuously while
 * scrollTop changes, so we can detect user direction + magnitude and
 * apply per-edge cooldowns. The resetKey guard ignores the
 * programmatic scroll-restoration events that fire right after a
 * channel switch (otherwise the saved-anchor restore would
 * immediately trigger a shift-down because the bottom of the small
 * slice is within thresholdPx of the viewport).
 */
export function useLoadMore({
  scrollRef,
  hasOlderTrimmed,
  hasMoreOnServer,
  hasNewerTrimmed,
  onShiftWindowUp,
  onShiftWindowDown,
  onBumpLimit,
  thresholdPx = DEFAULT_THRESHOLD_PX,
  cooldownMs = DEFAULT_COOLDOWN_MS,
  bumpInFlightRef,
  resetKey = null,
}: UseLoadMoreOptions) {
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const lastShiftUpAtRef = useRef(0);
  const lastShiftDownAtRef = useRef(0);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);

  // Derive: if bump is no longer in flight, loading is done.
  // eslint-disable-next-line react-hooks/refs -- intentional render-time derivation from caller's ref
  const loadingOlder = isLoadingOlder && bumpInFlightRef.current;

  // ─── resetKey guard state ──────────────────────────────────────
  // Programmatic scroll restoration after a channel-switch / jump
  // emits real scroll events; arm a brief window where we re-baseline
  // instead of acting on deltas.
  const skipScrollUntilRef = useRef(0);
  const prevScrollTopRef = useRef<number | null>(null);
  // Tracks `scrollHeight` at the time of the previous scroll event.
  // When a shift dispatch commits, the browser's `overflow-anchor`
  // bumps `scrollTop` to keep the visible anchor stable as content
  // grows above the viewport — and that bump emits a scroll event
  // with delta < 0 (scrollTop went UP). Without distinguishing it
  // from a user scroll, the down-direction branch treats it as a
  // genuine downward scroll, computes `distToBot` against the new
  // (larger) `scrollHeight`, and on tall viewports where
  // `distToBot < thresholdPx` it fires a spurious shift-down right
  // after a shift-up. The heuristic below catches the compensation by
  // its signature: scrollHeight grew AND scrollTop grew by ≈ the same
  // amount.
  const prevScrollHeightRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    skipScrollUntilRef.current = Date.now() + RESET_GUARD_MS;
    prevScrollTopRef.current = null;
    prevScrollHeightRef.current = null;
  }, [resetKey]);

  // ─── Latest-state refs for the scroll handler ──────────────────
  // Updated in an effect (not render) so eslint react-hooks/refs is
  // happy. The scroll handler can race up to one render behind the
  // latest props — harmless because shift-up at startIndex=0 and
  // shift-down at endIndex=len are no-ops in the dispatch reducer,
  // and the scroll listener fires many times so a stale-by-one-render
  // read self-corrects on the next event.
  const hasOlderTrimmedRef = useRef(hasOlderTrimmed);
  const hasMoreOnServerRef = useRef(hasMoreOnServer);
  const hasNewerTrimmedRef = useRef(hasNewerTrimmed);
  const onShiftWindowUpRef = useRef(onShiftWindowUp);
  const onShiftWindowDownRef = useRef(onShiftWindowDown);
  const onBumpLimitRef = useRef(onBumpLimit);

  useEffect(() => {
    hasOlderTrimmedRef.current = hasOlderTrimmed;
    hasMoreOnServerRef.current = hasMoreOnServer;
    hasNewerTrimmedRef.current = hasNewerTrimmed;
    onShiftWindowUpRef.current = onShiftWindowUp;
    onShiftWindowDownRef.current = onShiftWindowDown;
    onBumpLimitRef.current = onBumpLimit;
  });

  // ─── Unified scroll handler (both directions) ──────────────────
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;

    const handleScroll = () => {
      const now = Date.now();
      const curr = root.scrollTop;
      const curHeight = root.scrollHeight;

      // Inside guard window — re-baseline only, never act.
      if (now < skipScrollUntilRef.current) {
        prevScrollTopRef.current = curr;
        prevScrollHeightRef.current = curHeight;
        return;
      }

      // Throttled positional sample — top-level so it fires even when
      // the gesture is small (delta < hysteresis) and no decision is
      // taken. Gives the [sc] log a sense of time/movement between the
      // rarer shift/bump events.
      scrollTick(curr, curHeight, root.clientHeight);

      const prev = prevScrollTopRef.current;
      const prevHeight = prevScrollHeightRef.current;
      prevScrollTopRef.current = curr;
      prevScrollHeightRef.current = curHeight;
      if (prev === null) return;

      // Detect overflow-anchor compensation: scrollHeight grew (a shift
      // dispatch just committed, adding rows above the viewport) AND
      // scrollTop grew by approximately the same amount (the browser's
      // anchor-preservation). Not a user scroll — re-baseline and skip
      // so we don't fire a spurious shift-down off the post-commit
      // scroll event. Tight tolerance (10px) avoids false-positives
      // during incidental layout changes (image load, single-row
      // hydration) where the user's wheel delta wouldn't match the
      // height delta.
      if (prevHeight !== null) {
        const heightDelta = curHeight - prevHeight;
        const topDelta = curr - prev;
        if (
          heightDelta > 0 &&
          topDelta > 0 &&
          Math.abs(heightDelta - topDelta) < 10
        ) {
          scrollLog("skip oa-compensation", {
            heightDelta: heightDelta.toFixed(0),
            topDelta: topDelta.toFixed(0),
          });
          return;
        }
      }

      const delta = prev - curr; // positive when scrolling up
      // Gate on direction only — any actual scroll movement in a
      // direction is a real intent signal. Earlier this branch required
      // |delta| ≥ 8px between consecutive scroll events, which silently
      // broke slow scrolls: a 2-3px-per-tick scroll never accumulated
      // enough per-event delta to clear the threshold, so load-more
      // never fired and the user "got stuck" near the edge of the
      // rendered slice. Spam is bounded by the per-edge `cooldownMs`
      // (150ms), and overflow-anchor adjustments after shift dispatches
      // preserve `distanceToBottom`, so the opposite-direction check
      // doesn't fire spuriously from them either.
      if (delta === 0) return;

      // ─── Scroll-up direction ──────────────────────────────────
      if (delta > 0) {
        // Only act when within thresholdPx of the top of the scroll
        // content — matches the previous IntersectionObserver
        // rootMargin semantics and avoids spurious work mid-history.
        if (curr > thresholdPx) return;

        // Shift-up (reveal already-loaded trimmed rows) and bump
        // (fetch more from server) are NOT alternatives — they're two
        // ways of extending older history that operate in parallel.
        // The previous decideTopAction logic treated them as mutually
        // exclusive: shift-up first, then bump only after shift-up
        // exhausts the trimmed buffer. That works for slow scrolls
        // because each new scroll event re-runs the decision after
        // refs update, so the next event picks "bump" naturally. But
        // a fast wheel can cover the entire slice in ONE scroll event:
        // shift-up fires, scrollTop pins at the slice edge, browser
        // stops firing scroll events because scrollTop can't decrease
        // further, and the follow-up event that would have triggered
        // bump never arrives. User "hits a wall" — has to scroll down
        // + up to retrigger.
        //
        // Fix: fire both shift-up AND bump in parallel when both are
        // applicable. bumpInFlightRef throttles the server call to one
        // in-flight at a time (set in bumpLimit, cleared when Zero
        // delivers a larger slice OR by a 3s safety valve), so parallel
        // dispatch doesn't multiply server load.
        const canShiftUp = hasOlderTrimmedRef.current;
        const canBump = hasMoreOnServerRef.current && !bumpInFlightRef.current;

        if (canShiftUp) {
          if (now - lastShiftUpAtRef.current >= cooldownMs) {
            lastShiftUpAtRef.current = now;
            scrollLog("shift-up", { distToTop: curr.toFixed(0) });
            onShiftWindowUpRef.current();
          } else {
            scrollLog("shift-up cooldown", {
              wait: `${cooldownMs - (now - lastShiftUpAtRef.current)}ms`,
            });
          }
        }

        if (canBump) {
          scrollLog(canShiftUp ? "parallel-bump" : "bump-limit", {
            distToTop: curr.toFixed(0),
          });
          setIsLoadingOlder(true);
          onBumpLimitRef.current();
        }

        if (!canShiftUp && !canBump) {
          // hasOlderTrimmed=false AND (hasMoreOnServer=false OR
          // bumpInFlight=true). Either we're at absolute top of history
          // or a fetch is already racing for us.
          scrollLog("top-noop", {
            hasOlder: hasOlderTrimmedRef.current,
            hasMore: hasMoreOnServerRef.current,
            bumpInFlight: bumpInFlightRef.current,
          });
        }
        return;
      }

      // ─── Scroll-down direction (delta < 0) ────────────────────
      const distanceToBottom = root.scrollHeight - curr - root.clientHeight;
      if (distanceToBottom > thresholdPx) return;

      if (decideBottomAction(hasNewerTrimmedRef.current) === "shift-down") {
        if (now - lastShiftDownAtRef.current >= cooldownMs) {
          lastShiftDownAtRef.current = now;
          scrollLog("shift-down", { distToBot: distanceToBottom.toFixed(0) });
          onShiftWindowDownRef.current();
        } else {
          scrollLog("shift-down cooldown", {
            wait: `${cooldownMs - (now - lastShiftDownAtRef.current)}ms`,
          });
        }
      }
    };

    root.addEventListener("scroll", handleScroll, { passive: true });
    return () => root.removeEventListener("scroll", handleScroll);
  }, [scrollRef, thresholdPx, cooldownMs, bumpInFlightRef]);

  // ─── Wheel listener for at-edge gestures ───────────────────────
  // The scroll handler above can only react to `scroll` events, which
  // the browser fires when `scrollTop` actually moves. Once the user
  // is at scrollTop=0 (or maxScrollTop), wheeling further toward the
  // edge produces NO scroll event because there's no movement —
  // handler stays silent and loading stops. That's the "wall" the
  // user describes: a fast trackpad flick lands them at the slice
  // edge, and subsequent wheel attempts produce nothing until they
  // scroll back-then-forward to generate fresh scroll events.
  //
  // Wheel events fire regardless of whether the browser scrolls, so
  // we use them as a fallback for the at-edge case. When the user
  // wheels in the direction of an edge they're already against, fire
  // the same actions the scroll handler would have fired. Off-edge
  // wheels delegate to the scroll handler (early return) to avoid
  // double-dispatching.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;

    const handleWheel = (e: WheelEvent) => {
      const now = Date.now();
      if (now < skipScrollUntilRef.current) return;

      // Wheeling up
      if (e.deltaY < 0) {
        // Off-edge: scroll handler will fire normally.
        if (root.scrollTop > 5) return;

        const canShiftUp = hasOlderTrimmedRef.current;
        const canBump = hasMoreOnServerRef.current && !bumpInFlightRef.current;

        if (canShiftUp) {
          if (now - lastShiftUpAtRef.current >= cooldownMs) {
            lastShiftUpAtRef.current = now;
            scrollLog("wheel-edge shift-up", {});
            onShiftWindowUpRef.current();
          }
        }
        if (canBump) {
          scrollLog(
            canShiftUp ? "wheel-edge parallel-bump" : "wheel-edge bump-limit",
            {},
          );
          setIsLoadingOlder(true);
          onBumpLimitRef.current();
        }
        return;
      }

      // Wheeling down
      if (e.deltaY > 0) {
        const distToBot =
          root.scrollHeight - root.scrollTop - root.clientHeight;
        if (distToBot > 5) return;

        if (hasNewerTrimmedRef.current) {
          if (now - lastShiftDownAtRef.current >= cooldownMs) {
            lastShiftDownAtRef.current = now;
            scrollLog("wheel-edge shift-down", {});
            onShiftWindowDownRef.current();
          }
        }
      }
    };

    root.addEventListener("wheel", handleWheel, { passive: true });
    return () => root.removeEventListener("wheel", handleWheel);
  }, [scrollRef, cooldownMs, bumpInFlightRef]);

  return { topSentinelRef, bottomSentinelRef, isLoadingOlder: loadingOlder };
}
